import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { TodoList } from "@/components/todos/TodoList";
import { TodoForm } from "@/components/todos/TodoForm";
import { useApiClient, JobApiResponse, TodoCreate, TodoResponse, TodoUpdate } from "@/lib/api";
import { sortJobsByPriorityAndRecency } from "@/lib/job-status";
import { toast } from "sonner";
import { PlusCircle } from "lucide-react";

interface JobTodoCardProps {
  jobId: string;
}

export const JobTodoCard = ({ jobId }: JobTodoCardProps) => {
  const { api } = useApiClient();

  const [todos, setTodos] = useState<TodoResponse[]>([]);
  const [jobs, setJobs] = useState<JobApiResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<TodoResponse | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [todosResponse, jobsResponse] = await Promise.all([
        api.getTodos(true),
        api.getJobs(),
      ]);
      setTodos(Array.isArray(todosResponse) ? todosResponse : []);
      setJobs(Array.isArray(jobsResponse) ? sortJobsByPriorityAndRecency(jobsResponse) : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load todos");
      setTodos([]);
      setJobs([]);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleComplete = async (todoId: string, completed: boolean) => {
    try {
      const updated = await api.updateTodo(todoId, { completed });
      setTodos(prev => prev.map(t => (t.id === todoId ? updated : t)));
      toast.success(completed ? "Todo marked as complete!" : "Todo marked as incomplete");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update todo");
    }
  };

  const handleCreateTodo = async (data: TodoCreate) => {
    setIsSubmitting(true);
    try {
      const payload = { ...data, job_id: jobId } as TodoCreate;
      const created = await api.createTodo(payload);
      setTodos(prev => [created, ...prev]);
      setIsFormOpen(false);
      toast.success("Todo created successfully!");
    } catch (e) {
      console.error(e);
      toast.error("Failed to create todo");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTodo = async (data: TodoUpdate) => {
    if (!editingTodo) return;
    setIsSubmitting(true);
    try {
      const updated = await api.updateTodo(editingTodo.id, { ...data, job_id: jobId });
      setTodos(prev => prev.map(t => (t.id === editingTodo.id ? updated : t)));
      setEditingTodo(undefined);
      setIsFormOpen(false);
      toast.success("Todo updated successfully!");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update todo");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTodo = async (todoId: string) => {
    if (!confirm("Delete this todo?")) return;
    try {
      await api.deleteTodo(todoId);
      setTodos(prev => prev.filter(t => t.id !== todoId));
      toast.success("Todo deleted successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete todo");
    }
  };

  const handleReorderTodos = async (todoIds: string[]) => {
    try {
      await api.reorderTodos(todoIds);
      const reordered = todoIds
        .map(id => todos.find(t => t.id === id)!)
        .filter(Boolean) as TodoResponse[];
      setTodos(reordered);
    } catch (e) {
      console.error(e);
      toast.error("Failed to reorder todos");
      fetchData();
    }
  };

  const handleCreateNew = () => {
    setEditingTodo(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (todo: TodoResponse) => {
    setEditingTodo(todo);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingTodo(undefined);
  };

  return (
    <Card className="bg-white shadow-none sm:shadow-lg rounded-none sm:rounded-lg mx-0">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Todos</CardTitle>
        <Button size="sm" variant="outline" onClick={handleCreateNew}>
          <PlusCircle className="mr-2 h-4 w-4" /> Add
        </Button>
      </CardHeader>
      <CardContent>
        <TodoList
          todos={todos}
          jobs={jobs}
          onToggleComplete={handleToggleComplete}
          onEdit={handleEdit}
          onDelete={handleDeleteTodo}
          onReorder={handleReorderTodos}
          onCreateNew={handleCreateNew}
          isLoading={isLoading}
          embedded
          fixedJobId={jobId}
        />
      </CardContent>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <TodoForm
            todo={editingTodo}
            jobs={jobs}
            onSubmit={(data) => (editingTodo ? handleUpdateTodo(data as TodoUpdate) : handleCreateTodo(data as TodoCreate))}
            onCancel={handleCloseForm}
            isLoading={isSubmitting}
            embedded
            lockedJobId={jobId}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};
